import AbstractApiService from 'src/services/abstractApiService';
import { api, getClientAuthorization } from 'pages/APIs';
import { tokenRequest } from 'src/types';
import type { dynamicObj } from 'src/pages/assets';
import type {
  FeatureSubscription,
  FeatureSubscriptionCreateRequest,
  FeatureSubscriptionSuccessResponse,
} from 'src/sdk/auth/models/featureSubscription';
import { getOrgId } from 'src/utils/organization/org_id_helper';

export default class AuthApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async refreshToken(data: tokenRequest): Promise<any> {
    // ⭐ CRITICAL FIX: Bypass AbstractApiService to prevent header override
    // AbstractApiService.post() overwrites Content-Type to 'application/json' for non-FormData payloads
    // This breaks URLSearchParams encoding required by OAuth2 RFC 6749
    //
    // Solution: Use axios client directly (inherited from ApiClient)
    // - OAuth2 endpoint doesn't need org_id (user-level auth, not org-specific)
    // - Ensures proper application/x-www-form-urlencoded encoding per RFC 6749

    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    // ✅ RFC 6749 Compliance: Add client_id for public client authentication
    params.append('client_id', 'frontend');

    // Add refresh_token and other params
    for (const [key, value] of Object.entries(data)) {
      // Skip grant_type if it somehow exists in data - we always use 'refresh_token'
      if (key !== 'grant_type') {
        params.append(key, value);
      }
    }

    try {
      // Use axios client directly (inherited from ApiClient) instead of super.post()
      // This bypasses AbstractApiService's content-type override and org_id injection
      const response = await (this as any).client.post(api.get_token, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          // RFC 6749: Public client authentication uses client_id parameter only
          // Do NOT include Authorization header for public clients (token refresh also uses public client)
        },
      });
      return response.data;
    } catch (error: any) {
      // Re-throw the error response data for proper error handling
      throw error.response?.data || error;
    }
  }

  public async setUserSession(): Promise<any> {
    return super.post(api.set_session_user, new FormData());
  }

  public async setDocFlowSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_docflow_subscription, formData);
  }

  public async setBODStreamSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_bod_stream_subscription, formData);
  }

  public async setBODSteamSubscriptionUsage(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_bod_stream_subscription_usage, formData);
  }

  public async deleteBODStreamSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.delete_bod_stream_subscription, formData);
  }

  public async setSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_subscription, formData);
  }

  public async setSubscriptionUsage(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.create_subscription_usage, formData);
  }

  public async getSubscriptionPlans(): Promise<any> {
    return super.get(api.get_all_subscription_plans);
  }

  public async endSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.end_subscription, formData);
  }

  public async getSubscription(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value);
    }
    return super.post(api.get_subscriptions_for_app, formData);
  }

  // Feature subscription lifecycle endpoints

  public async createFeatureSubscription(
    data: FeatureSubscriptionCreateRequest,
  ): Promise<FeatureSubscription> {
    const formData = new FormData();
    formData.append('plan_id', data.plan_id);
    formData.append('start_date', data.start_date);
    if (data.end_date) {
      formData.append('end_date', data.end_date);
    }
    return super.post(api.feature_subscription_create, formData);
  }

  public async listFeatureSubscriptions(): Promise<FeatureSubscription[]> {
    return super.get(api.feature_subscription_list);
  }

  public async cancelFeatureSubscription(
    subId: string,
  ): Promise<FeatureSubscriptionSuccessResponse> {
    return super.postWithoutOrgId(
      `${api.feature_subscription_base}${subId}/cancel`,
      {},
    );
  }

  public async reactivateFeatureSubscription(
    subId: string,
  ): Promise<FeatureSubscriptionSuccessResponse> {
    return super.postWithoutOrgId(
      `${api.feature_subscription_base}${subId}/reactivate`,
      {},
    );
  }

}
